import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendSocketMessage, isSocketConnected } from '../services/socket';
import { sendMessageRest } from '../redux/actions/chatRealtime';
import {
    addOptimisticMessage,
    confirmMessage,
    failMessage,
    retryMessageStart,
} from '../redux/reducers/chatRealtime';

const generateClientId = () => `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const useSendMessage = (courseId) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.user);

    const deliver = useCallback(async ({ conversationId, content, type, attachments, replyTo, clientId, mentions }) => {
        const payload = {
            courseId,
            conversationId,
            content,
            type,
            attachments,
            replyToMessageId: replyTo?._id || replyTo?.messageId,
            clientId,
            mentionIds: mentions,
        };

        try {
            let message = null;

            if (isSocketConnected()) {
                const response = await sendSocketMessage(payload);
                message = response?.message;
            } else {
                message = await dispatch(sendMessageRest(conversationId, payload));
            }

            if (message) {
                dispatch(confirmMessage({ clientId, message }));
            } else {
                dispatch(failMessage({ clientId }));
            }
        } catch (err) {
            console.warn('[Chat] Error al enviar:', err.message);
            dispatch(failMessage({ clientId }));
        }
    }, [courseId, dispatch]);

    const sendMessage = useCallback(({ conversationId, content, type = 'text', attachments = [], replyTo = null, mentions = [] }) => {
        if (!conversationId) return;

        const clientId = generateClientId();

        const optimisticMessage = {
            _id: clientId,
            clientId,
            conversationId,
            courseId,
            content,
            type,
            attachments,
            mentions,
            replyTo: replyTo
                ? {
                    messageId: replyTo._id || replyTo.messageId,
                    content: replyTo.content,
                    senderId: replyTo.sender?._id || replyTo.senderId,
                    sender: replyTo.sender
                        ? {
                            _id: replyTo.sender._id,
                            firstName: replyTo.sender.firstName,
                            lastName: replyTo.sender.lastName,
                        }
                        : undefined,
                    type: replyTo.type,
                }
                : null,
            sender: {
                _id: user?._id,
                firstName: user?.firstName,
                lastName: user?.lastName,
                avatarUrl: user?.avatarUrl,
                typeUser: user?.typeUser,
            },
            createdAt: new Date().toISOString(),
        };

        // Mostrar de inmediato en la lista (UI optimista)
        dispatch(addOptimisticMessage(optimisticMessage));

        // Envío en segundo plano
        deliver({ conversationId, content, type, attachments, replyTo, clientId, mentions });

        return clientId;
    }, [courseId, dispatch, deliver, user]);

    const retryMessage = useCallback((message) => {
        if (!message?.clientId) return;
        dispatch(retryMessageStart({ clientId: message.clientId }));
        deliver({
            conversationId: message.conversationId,
            content: message.content,
            type: message.type,
            attachments: message.attachments || [],
            replyTo: message.replyTo,
            clientId: message.clientId,
            mentions: message.mentions || [],
        });
    }, [deliver, dispatch]);

    return { sendMessage, retryMessage };
};
